/**
 * Compact neighbourhood location landings for Tools & Bubbles local SEO.
 * Each page is unique copy for a high-intent Kochi locality not covered by the
 * older long-form configs. Super-admin can override titles/descriptions via
 * public marketing landing-page SEO.
 */
import type { LocationPageConfig, NeighbourhoodSpec } from './locationPageConfig.types.js';
import { mergeHyperLocalFaqs } from '../../utils/locationHyperLocalFaqs.js';
import { KOCHI_MORE_HYPERLOCAL_SPECS } from './kochiMoreHyperLocalSpecs.js';
import { KOCHI_RADIUS_50KM_SPECS, KOCHI_RADIUS_SPELLING_ALIASES } from './kochiRadius50kmSpecs.js';

function createNeighbourhoodLocation(spec: NeighbourhoodSpec): LocationPageConfig {
  const { name, slug } = spec;
  return {
    slug,
    locationName: name,
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: spec.eyebrow,
      title: `Cleaning Service in ${name}`,
      description: spec.blurb,
      highlights: [
        { icon: spec.highlightIcon, text: spec.highlightText },
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Star', text: '4.8★ Google rated' },
      ],
      bookNowLocation: spec.key,
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'MapPin',
        title: `Local ${name} crew`,
        description: `A trained team that already works ${spec.nearby.slice(0, 2).join(' and ')} routes — less travel time, more cleaning time.`,
      },
      {
        icon: 'Shield',
        title: 'Insured, background-verified staff',
        description: `Apartment associations and gated communities in ${name} get supervisor check-ins, 3M chemicals and Kärcher equipment.`,
      },
      {
        icon: 'Sparkles',
        title: 'Transparent online pricing',
        description:
          'See the quote before you confirm. No hidden travel fees inside Greater Kochi when you book online.',
      },
    ],
    serviceAreas: spec.areas,
    localLandmarks: spec.landmarks,
    reviews: spec.reviews.map((review) => ({
      ...review,
      rating: 5,
      date: '2026',
    })),
    relatedLocations: spec.related.map((item) => ({
      name: item.name,
      description: `Cleaning service in ${item.name}`,
      href: item.href,
    })),
    cta: {
      title: `Book ${name} cleaning now`,
      description: `Same-day and next-day deep cleaning, home cleaning and sofa cleaning in ${name}.`,
      phoneText: 'Call 9995216660',
    },
    seo: {
      introHeading: `Professional cleaning service in ${name}, Kochi`,
      introParagraphs: [
        `Tools & Bubbles is a professional cleaning service in ${name}, Kochi. We handle deep cleaning, regular home cleaning, kitchen and bathroom sanitisation, sofa shampoo, and office housekeeping for apartments, villas and small offices around ${spec.landmarks[0] ?? name}.`,
        spec.blurb,
        `Search “cleaning service ${name}”, “deep cleaning ${name}” or “home cleaning near me ${name}” and book online — or call 9995216660. Crews carry Kärcher equipment and 3M chemicals, and we coordinate with society security when needed.`,
        `We do not invent a fake ${name} shopfront. The Google Business pin stays at Bose Nagar, Kadavanthara (682020). ${name} is a service area we dispatch to — that is how map-pack consistency works across every neighbourhood.`,
      ],
      sections: [
        {
          heading: `Cleaning services available in ${name}`,
          paragraphs: [
            `From one-time deep cleaning before guests arrive to recurring weekly housekeeping, the ${name} team covers the full job: dusting, mopping, kitchen degrease, bathroom anti-fungal treatment, balcony wash and optional sofa or mattress add-ons.`,
          ],
          bulletList: [
            `Deep cleaning ${name}`,
            `Home / apartment cleaning ${name}`,
            'Kitchen & bathroom deep clean',
            'Sofa and upholstery shampoo',
            'Same-day cleaning when booked before noon',
            'Move-in / post-renovation clean',
            'Office and small-clinic housekeeping',
          ],
        },
        {
          heading: `Why ${name} homes book professional cleaners`,
          paragraphs: [
            `Kochi’s humidity, construction dust and monsoon mould hit ${name} flats as hard as anywhere in Ernakulam. Working professionals around ${spec.nearby[0] ?? name} need a crew that can arrive while they are at work and leave photo proof on WhatsApp.`,
            `High-rises near ${spec.landmarks[0] ?? name} collect balcony grit and AC-grill dust. Independent houses pick up courtyard moss. We schedule the right machine for the property type — not a mop-only visit.`,
          ],
        },
        {
          heading: `Neighbourhood coverage in ${name}`,
          paragraphs: [
            `We cover ${spec.areas.join(', ')} and neighbouring pockets toward ${spec.nearby.join(', ')}. If your pin is slightly outside the list, call 9995216660 and we will confirm a slot the same day.`,
            `Landmarks our ${name} crews already know: ${spec.landmarks.join(', ')}. Share the society name or Google pin when you book so the supervisor can arrange a gate pass.`,
          ],
        },
        {
          heading: `Apartments, villas and offices in ${name}`,
          paragraphs: [
            `Apartment associations in ${name} usually want ID, a time window and a supervisor check-out. Villas want outdoor sit-outs and water-tank access. Small offices around ${spec.nearby[0] ?? name} want after-hours pantry and restroom work. Tell us the property type on the form and we send the matching kit.`,
          ],
        },
        {
          heading: `Monsoon and humidity care in ${name}`,
          paragraphs: [
            `${name} bathrooms grow fungus on grout through June–September. We use anti-fungal bathroom treatment, extract sofas that smell musty, and dry floors so you are not walking on wet residue. Recurring fortnightly plans are the cheapest way to stay ahead of monsoon.`,
          ],
        },
        {
          heading: 'How to book a slot',
          paragraphs: [
            `Book online, WhatsApp or call 9995216660. Same-day ${name} cleaning is first-come, first-served before 2 PM. Recurring fortnightly plans keep bathrooms fungus-free through monsoon.`,
            `After the job we can send a Google review link. Honest reviews from ${name} customers are how neighbourhood pages and the Google Business Profile stay trustworthy — we never buy fake ratings.`,
          ],
        },
      ],
      conclusion: `Book cleaning service in ${name} today. Transparent pricing, insured team, same-day slots, dispatched from Kadavanthara. Call +91 9995216660.`,
    },
    faq: mergeHyperLocalFaqs(
      [
        {
          question: `Do you offer cleaning service in ${name}?`,
          answer: `Yes. Tools & Bubbles provides deep cleaning, home cleaning, sofa cleaning and office housekeeping in ${name} and nearby ${spec.nearby.join(', ')}. Book online or call 9995216660.`,
        },
        {
          question: `Is same-day deep cleaning available in ${name}?`,
          answer: `Same-day slots in ${name} are usually available when you book before noon, subject to crew load. Next-day morning slots are the fallback.`,
        },
        {
          question: `Which areas near ${name} do you cover?`,
          answer: `We cover ${spec.areas.join(', ')}. Neighbouring ${spec.nearby.join(', ')} are on the same routing.`,
        },
        {
          question: `How much does home cleaning cost in ${name}?`,
          answer: `Pricing is shown on the booking form by home size (1BHK–villa). ${name} jobs use the same Greater Kochi rate card — no surprise locality surcharge when you book online.`,
        },
        {
          question: `Do you have a shop in ${name}?`,
          answer: `No fake ${name} storefront. The registered office and Google pin stay at Bose Nagar, Kadavanthara 682020. We dispatch an insured crew to ${name} as a service area so the map pack stays consistent.`,
        },
        {
          question: `Can you enter a gated apartment in ${name}?`,
          answer: `Yes. Share the society name, tower and any gate-pass rules when you book. The supervisor carries ID and coordinates with security around ${spec.landmarks[0] ?? name}.`,
        },
        {
          question: `How long does a 2BHK deep clean take in ${name}?`,
          answer: `Most ${name} 2BHK deep cleans take 4–6 hours depending on kitchen grease and bathroom grout. Villas and post-renovation jobs run longer — the booking form shows a time window.`,
        },
      ],
      { locationName: name, areasMention: spec.areas, landmarks: spec.landmarks },
    ),
  };
}

const SPECS: NeighbourhoodSpec[] = [
  {
    key: 'vennala',
    name: 'Vennala',
    slug: 'cleaning-service-vennala',
    eyebrow: 'Vennala High School Rd, Palarivattom & Bypass',
    blurb:
      'Deep cleaning and home cleaning in Vennala, Kochi — apartments near the bypass, family houses off School Road, and offices toward Palarivattom. Same-day slots with transparent online pricing.',
    highlightIcon: 'Bus',
    highlightText: 'Bypass & School Road',
    areas: [
      'Vennala Junction',
      'Vennala High School Road',
      'Palarivattom Bypass',
      'Mamangalam',
      'Ponekkara edge',
    ],
    landmarks: ['Vennala High School', 'Palarivattom Bypass', 'Changampuzha Park approach'],
    nearby: ['Palarivattom', 'Edappally', 'Kaloor'],
    related: [
      { name: 'Palarivattom', href: '/cleaning-service-palarivattom' },
      { name: 'Edappally', href: '/cleaning-service-edappally' },
      { name: 'Kaloor', href: '/cleaning-service-kaloor' },
    ],
    reviews: [
      {
        name: 'Nisha R.',
        location: 'Vennala',
        text: 'Booked a 2BHK deep clean before Onam. Kitchen grease was gone and bathrooms smelled fresh.',
      },
      {
        name: 'Arun M.',
        location: 'School Road',
        text: 'Crew coordinated with our apartment security. Same-day slot after a noon booking.',
      },
      {
        name: 'Fathima S.',
        location: 'Vennala',
        text: 'Sofa shampoo + home clean in one visit. Fair price shown online.',
      },
    ],
  },
  {
    key: 'elamkulam',
    name: 'Elamkulam',
    slug: 'cleaning-service-elamkulam',
    eyebrow: 'SA Road, Kadavanthra & Elamkulam Metro',
    blurb:
      'Cleaning service in Elamkulam for metro-side apartments, SA Road offices and family homes toward Kadavanthra. Deep cleaning, recurring home cleaning and sofa shampoo with same-day booking.',
    highlightIcon: 'Train',
    highlightText: 'Elamkulam Metro',
    areas: ['Elamkulam Metro', 'SA Road Elamkulam', 'Girinagar', 'Jawahar Nagar edge', 'Kadavanthra border'],
    landmarks: ['Elamkulam Metro Station', 'SA Road', 'Girinagar'],
    nearby: ['Kadavanthra', 'Vyttila', 'Panampilly Nagar'],
    related: [
      { name: 'Kadavanthra', href: '/cleaning-service-kadavanthra' },
      { name: 'Vyttila', href: '/cleaning-service-vyttila' },
      { name: 'Panampilly Nagar', href: '/cleaning-service-panampilly-nagar' },
    ],
    reviews: [
      {
        name: 'Meera K.',
        location: 'Elamkulam',
        text: 'Metro apartment deep clean while I was at work. Photos on WhatsApp when they finished.',
      },
      {
        name: 'Rohan P.',
        location: 'Girinagar',
        text: 'Bathroom anti-fungal treatment before monsoon. Noticeable difference on grout.',
      },
      {
        name: 'Anitha V.',
        location: 'SA Road',
        text: 'Office pantry and restroom contract. Punctual every week.',
      },
    ],
  },
  {
    key: 'thevara',
    name: 'Thevara',
    slug: 'cleaning-service-thevara',
    eyebrow: 'Thevara Junction, Waterfront & Perumanoor',
    blurb:
      'Villa and apartment cleaning in Thevara, Kochi. Waterfront humidity needs extra bathroom and balcony care — we deep-clean homes, homestays and small offices around Thevara Junction.',
    highlightIcon: 'Waves',
    highlightText: 'Waterfront homes',
    areas: ['Thevara Junction', 'Perumanoor', 'Thevara Ferry', 'Atlantic', 'Kundanoor approach'],
    landmarks: ['Thevara Junction', 'Thevara Ferry', 'Waterfront apartments'],
    nearby: ['Panampilly Nagar', 'Kadavanthra', 'Maradu'],
    related: [
      { name: 'Panampilly Nagar', href: '/cleaning-service-panampilly-nagar' },
      { name: 'Kadavanthra', href: '/cleaning-service-kadavanthra' },
      { name: 'Maradu', href: '/cleaning-service-maradu' },
    ],
    reviews: [
      {
        name: 'Joseph A.',
        location: 'Thevara',
        text: 'Villa deep clean before family from the Gulf arrived. Balconies and windows were spotless.',
      },
      {
        name: 'Lakshmi N.',
        location: 'Perumanoor',
        text: 'Kitchen chimney degrease was the highlight. Coconut-oil film actually came off.',
      },
      {
        name: 'Sandeep T.',
        location: 'Thevara',
        text: 'Move-in clean for a waterfront flat. Dust from renovation was fully removed.',
      },
    ],
  },
  {
    key: 'palluruthy',
    name: 'Palluruthy',
    slug: 'cleaning-service-palluruthy',
    eyebrow: 'West Kochi, Palluruthy Market & Aroor Road',
    blurb:
      'Home and shop cleaning in Palluruthy, West Kochi. Deep cleaning for family houses, church-side homes and small commercial spaces toward Fort Kochi and Aroor.',
    highlightIcon: 'HeartHandshake',
    highlightText: 'West Kochi locals',
    areas: [
      'Palluruthy Market',
      'Palluruthy Church Road',
      'Kumbalam approach',
      'Aroor Road',
      'Thoppumpady edge',
    ],
    landmarks: ['Palluruthy Market', 'Palluruthy Church', 'Aroor Road'],
    nearby: ['Fort Kochi', 'Thoppumpady', 'Kumbalangi'],
    related: [
      { name: 'Fort Kochi', href: '/cleaning-service-fort-kochi' },
      { name: 'Kumbalangi', href: '/cleaning-service-kumbalangi' },
      { name: 'Willingdon Island', href: '/cleaning-service-willingdon-island' },
    ],
    reviews: [
      {
        name: 'Shiny P.',
        location: 'Palluruthy',
        text: 'Festival clean before Christmas. Team was respectful in a traditional home.',
      },
      {
        name: 'Rajan C.',
        location: 'Market Road',
        text: 'Shop floor scrub plus house deep clean the same week. Easy booking.',
      },
      { name: 'Beena J.', location: 'Palluruthy', text: 'Bathroom tiles look new after grout treatment.' },
    ],
  },
  {
    key: 'vazhakkala',
    name: 'Vazhakkala',
    slug: 'cleaning-service-vazhakkala',
    eyebrow: 'Thrikkakara, Infopark corridor & Padamugal',
    blurb:
      'Cleaning service in Vazhakkala for Infopark-side apartments, Padamugal homes and Thrikkakara families. Deep cleaning that handles construction dust from the Smart City corridor.',
    highlightIcon: 'Building',
    highlightText: 'Infopark corridor',
    areas: ['Vazhakkala Junction', 'Padamugal', 'Thrikkakara', 'Infopark residential', 'NGO Quarters edge'],
    landmarks: ['Vazhakkala Junction', 'Padamugal', 'Infopark approach'],
    nearby: ['Kakkanad', 'Thrikkakara', 'Edappally'],
    related: [
      { name: 'Kakkanad', href: '/cleaning-service-kakkanad' },
      { name: 'Thrikkakara', href: '/cleaning-service-thrikkakara' },
      { name: 'Edappally', href: '/cleaning-service-edappally' },
    ],
    reviews: [
      {
        name: 'Vivek S.',
        location: 'Vazhakkala',
        text: 'High-rise near Infopark. Dust on window tracks and AC grills was the main issue — they got it.',
      },
      {
        name: 'Divya L.',
        location: 'Padamugal',
        text: 'Fortnightly plan for a 3BHK. Bathrooms stay fungus-free in monsoon.',
      },
      {
        name: 'Ajith R.',
        location: 'Vazhakkala',
        text: 'Post-renovation clean. Cement haze on tiles is gone.',
      },
    ],
  },
  {
    key: 'thammanam',
    name: 'Thammanam',
    slug: 'cleaning-service-thammanam',
    eyebrow: 'Thammanam–Pullepady, Kaloor & Palarivattom',
    blurb:
      'Apartment and independent-house cleaning in Thammanam, Kochi. Convenient for Pullepady, Kaloor Stadium side and Palarivattom commuters who need weekday afternoon slots.',
    highlightIcon: 'MapPin',
    highlightText: 'Kaloor–Palarivattom belt',
    areas: [
      'Thammanam Junction',
      'Pullepady',
      'Deshabhimani Road edge',
      'Kaloor Stadium approach',
      'Palarivattom west',
    ],
    landmarks: ['Thammanam Junction', 'Pullepady', 'JLN Stadium approach'],
    nearby: ['Kaloor', 'Palarivattom', 'Kadavanthra'],
    related: [
      { name: 'Kaloor', href: '/cleaning-service-kaloor' },
      { name: 'Palarivattom', href: '/cleaning-service-palarivattom' },
      { name: 'Kadavanthra', href: '/cleaning-service-kadavanthra' },
    ],
    reviews: [
      {
        name: 'Priya D.',
        location: 'Thammanam',
        text: 'Booked while at office. 2BHK ready before I got home at 7.',
      },
      { name: 'Manoj K.', location: 'Pullepady', text: 'Kitchen and bathroom focus package. Fair and fast.' },
      {
        name: 'Sneha I.',
        location: 'Thammanam',
        text: 'Sofa + mattress in the same visit. Dust-mite vacuum made a difference for allergies.',
      },
    ],
  },
  {
    key: 'elamakkara',
    name: 'Elamakkara',
    slug: 'cleaning-service-elamakkara',
    eyebrow: 'Elamakkara Road, Ponekkara & Edappally',
    blurb:
      'Cleaning service in Elamakkara for family houses, church-side homes and apartments toward Ponekkara and Edappally Metro. Deep cleaning with Kerala-humidity bathroom care.',
    highlightIcon: 'Users',
    highlightText: 'Family neighbourhoods',
    areas: ['Elamakkara Road', 'Ponekkara', 'Mamangalam edge', 'Edappally south', 'North Railway approach'],
    landmarks: ['Elamakkara Church', 'Ponekkara', 'Edappally Metro approach'],
    nearby: ['Edappally', 'Kaloor', 'Palarivattom'],
    related: [
      { name: 'Edappally', href: '/cleaning-service-edappally' },
      { name: 'Kaloor', href: '/cleaning-service-kaloor' },
      { name: 'Palarivattom', href: '/cleaning-service-palarivattom' },
    ],
    reviews: [
      {
        name: 'Tessy M.',
        location: 'Elamakkara',
        text: 'Independent house deep clean including outdoor sit-out. Team was thorough.',
      },
      {
        name: 'Nikhil B.',
        location: 'Ponekkara',
        text: 'Regular monthly clean for parents’ home. Reliable WhatsApp updates.',
      },
      {
        name: 'Anu G.',
        location: 'Elamakkara',
        text: 'Pre-Onam clean. Kitchen cabinets degreased properly.',
      },
    ],
  },
  {
    key: 'cheranalloor',
    name: 'Cheranalloor',
    slug: 'cleaning-service-cheranalloor',
    eyebrow: 'Cheranalloor, Edappally North & Varapuzha Road',
    blurb:
      'Home cleaning in Cheranalloor, Ernakulam — river-side houses, new apartments and NH-side homes toward Edappally and Varapuzha. Same-day deep cleaning when slots are open.',
    highlightIcon: 'Waves',
    highlightText: 'North Kochi river belt',
    areas: [
      'Cheranalloor Town',
      'Varapuzha Road',
      'Edappally North',
      'Chittoor edge',
      'Container Road approach',
    ],
    landmarks: ['Cheranalloor Junction', 'Varapuzha bridge approach', 'Edappally North'],
    nearby: ['Edappally', 'Eloor', 'Aluva'],
    related: [
      { name: 'Edappally', href: '/cleaning-service-edappally' },
      { name: 'Eloor', href: '/cleaning-service-eloor' },
      { name: 'Aluva', href: '/cleaning-service-aluva' },
    ],
    reviews: [
      {
        name: 'George P.',
        location: 'Cheranalloor',
        text: 'River-side humidity in bathrooms — anti-fungal treatment worked.',
      },
      {
        name: 'Rameeza A.',
        location: 'Cheranalloor',
        text: 'New apartment move-in clean. Windows and kitchen were the priority.',
      },
      {
        name: 'Biju T.',
        location: 'Varapuzha Road',
        text: 'Booked two houses the same week. Both crews arrived on time.',
      },
    ],
  },
  {
    key: 'nettoor',
    name: 'Nettoor',
    slug: 'cleaning-service-nettoor',
    eyebrow: 'Nettoor, Kundanoor & Maradu lakeside',
    blurb:
      'Villa and apartment cleaning in Nettoor, Kochi. Lakeside humidity, high-rises near Kundanoor Junction and family homes toward Maradu — deep cleaning built for this belt.',
    highlightIcon: 'Building',
    highlightText: 'Kundanoor high-rises',
    areas: ['Nettoor', 'Kundanoor Junction', 'Maradu lakeside', 'Thevara approach', 'NH Bypass Nettoor'],
    landmarks: ['Kundanoor Junction', 'Nettoor lake', 'Maradu Bypass'],
    nearby: ['Maradu', 'Thevara', 'Vyttila'],
    related: [
      { name: 'Maradu', href: '/cleaning-service-maradu' },
      { name: 'Vyttila', href: '/cleaning-service-vyttila' },
      { name: 'Thevara', href: '/cleaning-service-thevara' },
    ],
    reviews: [
      {
        name: 'Aravind S.',
        location: 'Nettoor',
        text: 'High-rise 3BHK. Window tracks and balcony railings were filthy from lake dust — now clean.',
      },
      {
        name: 'Neethu R.',
        location: 'Kundanoor',
        text: 'Same-day kitchen + bathroom before guests. Exactly what we needed.',
      },
      {
        name: 'Paul V.',
        location: 'Nettoor',
        text: 'Villa floor scrub with the machine. Much better than mop-only cleaners.',
      },
    ],
  },
  {
    key: 'irumpanam',
    name: 'Irumpanam',
    slug: 'cleaning-service-irumpanam',
    eyebrow: 'Irumpanam, Thripunithura & Seaport-Airport Road',
    blurb:
      'Cleaning service in Irumpanam for Seaport-Airport Road apartments, Thripunithura-side villas and new townships. Deep cleaning that keeps up with construction dust on SPA Road.',
    highlightIcon: 'Bus',
    highlightText: 'SPA Road corridor',
    areas: [
      'Irumpanam Junction',
      'Seaport-Airport Road',
      'Thripunithura east',
      'Chottanikkara approach',
      'Puthiyakavu edge',
    ],
    landmarks: ['Irumpanam Junction', 'Seaport-Airport Road', 'Thripunithura'],
    nearby: ['Thripunithura', 'Vazhakkala', 'Maradu'],
    related: [
      { name: 'Thripunithura', href: '/cleaning-service-thripunithura' },
      { name: 'Maradu', href: '/cleaning-service-maradu' },
      { name: 'Vazhakkala', href: '/cleaning-service-vazhakkala' },
    ],
    reviews: [
      {
        name: 'Karthika N.',
        location: 'Irumpanam',
        text: 'New flat on SPA Road. Post-construction residue needed a real crew, not a broom.',
      },
      {
        name: 'Suresh P.',
        location: 'Irumpanam',
        text: 'Monthly villa clean. Garden sit-out and interiors both handled.',
      },
      {
        name: 'Asha M.',
        location: 'Thripunithura east',
        text: 'Booked from the Thripunithura page, crew knew Irumpanam access well.',
      },
    ],
  },
];

const ALL_NEIGHBOURHOOD_SPECS: NeighbourhoodSpec[] = [
  ...SPECS,
  ...KOCHI_RADIUS_50KM_SPECS,
  ...KOCHI_MORE_HYPERLOCAL_SPECS,
];

export const NEIGHBOURHOOD_LOCATION_CONFIGS: Record<string, LocationPageConfig> = Object.fromEntries(
  ALL_NEIGHBOURHOOD_SPECS.map((spec) => [spec.key, createNeighbourhoodLocation(spec)]),
);

export const NEIGHBOURHOOD_HOME_CLEANING_ALIASES: Record<string, string> = {
  ...Object.fromEntries(
    ALL_NEIGHBOURHOOD_SPECS.map((spec) => [
      `home-cleaning-${spec.slug.replace('cleaning-service-', '')}`,
      spec.slug,
    ]),
  ),
  ...KOCHI_RADIUS_SPELLING_ALIASES,
};
